import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLongLeftIcon, ArrowRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import cn from 'classnames';

import { getCategorySelectionState, hasCategoryServices, toggleCategorySelection, toggleServiceId } from 'bl/consultationForms/consultationFormServiceSelection.util';
import { fetchSalonCategoriesIfNeeded } from 'bl/consultationForms/fetchSalonCategoriesIfNeeded.util';
import { resolveConsultationFormRules } from 'bl/consultationForms/resolveConsultationFormRules.util';
import Button from 'components/form/Button.component';
import TypedOption from 'components/form/TypedOption.component';
import LINK from 'constants/link.const';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectSalonServicesCategories, selectSalonServicesCategoriesAreFetched, selectSalonServicesCategoriesFetchError, selectSalonServicesCategoriesIsFetching } from 'selectors/salon.selectors';
import linkTo from 'utils/links/linkTo.util';

import { getCategories } from 'salon/actions';

import { feSetConsultationFormRules } from '../consultationForms.action';
import {
  EXPIRATION_PERIOD,
  EXPIRATION_PERIOD_OPTIONS,
  FORM_AUTOMATION_TYPES,
  FORM_FREQUENCY,
  FREQUENCY_OPTIONS,
  RULE_FIELD_KEYS,
  SERVICE_SELECTION,
  TRIGGER_TIMING_OPTIONS,
} from '../consultationForms.const';
import { selectConsultationFormById } from '../consultationForms.selectors';

import { RulesSelect } from './RulesSelect.component';

import CN from './SetRulesPage.module.scss';

export const SetRulesPage = () => {
  const { t } = useTranslation();
  const { formId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const salonId = useCurrentSalonId();
  const { isDarkMode } = useTheme();
  const isMobile = useIsMobileViewport();

  const existingForm = useSelector((state) => selectConsultationFormById(state, formId));
  const rawCategories = useSelector(selectSalonServicesCategories);
  const categories = useMemo(() => rawCategories ?? [], [rawCategories]);
  const areCategoriesFetched = useSelector(selectSalonServicesCategoriesAreFetched);
  const isCategoriesFetching = useSelector(selectSalonServicesCategoriesIsFetching);
  const categoriesFetchError = useSelector(selectSalonServicesCategoriesFetchError);

  useEffect(() => {
    fetchSalonCategoriesIfNeeded({ salonId, areCategoriesFetched, dispatch, getCategories });
  }, [salonId, areCategoriesFetched, dispatch]);

  const { handleSubmit, watch, setValue } = useForm({
    defaultValues: resolveConsultationFormRules(existingForm?.rules),
  });

  const triggerType = watch(RULE_FIELD_KEYS.TRIGGER_TYPE);
  const serviceSelectionType = watch(RULE_FIELD_KEYS.SERVICE_SELECTION_TYPE);
  const rawServiceIds = watch(RULE_FIELD_KEYS.SERVICE_IDS);
  const serviceIds = useMemo(() => rawServiceIds ?? [], [rawServiceIds]);
  const triggerTiming = watch(RULE_FIELD_KEYS.TRIGGER_TIMING);
  const frequency = watch(RULE_FIELD_KEYS.FREQUENCY);
  const expirationPeriod = watch(RULE_FIELD_KEYS.EXPIRATION_PERIOD);

  const isManual = triggerType === FORM_AUTOMATION_TYPES.MANUAL;
  const isEveryAppointment = frequency === FORM_FREQUENCY.EVERY_APPOINTMENT;
  const showExpiryField = isManual || !isEveryAppointment;
  const expiryMonths = Number(expirationPeriod?.split('_')[0]);
  const isSelectedServices = serviceSelectionType === SERVICE_SELECTION.SELECTED;

  const totalServicesCount = useMemo(() => categories.reduce((sum, cat) => sum + (cat.services?.length ?? 0), 0), [categories]);
  const hasNoServices = areCategoriesFetched && totalServicesCount === 0;
  const hasServicesError = isSelectedServices && areCategoriesFetched && totalServicesCount > 0 && serviceIds.length === 0;
  const isFooterBlocked = hasServicesError || (isSelectedServices && (isCategoriesFetching || !!categoriesFetchError));
  const timingOptions = TRIGGER_TIMING_OPTIONS[triggerType] ?? [];

  const categoriesWithServices = useMemo(() => categories.filter(hasCategoryServices), [categories]);

  const serviceCategoryColumns = useMemo(() => {
    if (isMobile) return [categoriesWithServices];

    return [categoriesWithServices.filter((_, index) => index % 2 === 0), categoriesWithServices.filter((_, index) => index % 2 === 1)];
  }, [categoriesWithServices, isMobile]);

  const builderBasePath = linkTo({ pattern: LINK.settingsFormBuilder, params: { salonId, formId } });
  const formsListPath = linkTo({ pattern: LINK.settingsForms, params: { salonId } });

  const handleTriggerTypeChange = useCallback(
    (type) => {
      setValue(RULE_FIELD_KEYS.TRIGGER_TYPE, type);
      if (type === FORM_AUTOMATION_TYPES.MANUAL) {
        setValue(RULE_FIELD_KEYS.TRIGGER_TIMING, '');
      } else {
        const firstOption = TRIGGER_TIMING_OPTIONS[type]?.[0]?.value ?? '';
        setValue(RULE_FIELD_KEYS.TRIGGER_TIMING, firstOption);
      }
    },
    [setValue],
  );

  const handleServiceSelectionChange = useCallback(
    (type) => {
      setValue(RULE_FIELD_KEYS.SERVICE_SELECTION_TYPE, type);
      if (type === SERVICE_SELECTION.ALL) setValue(RULE_FIELD_KEYS.SERVICE_IDS, []);
    },
    [setValue],
  );

  useEffect(() => {
    if (hasNoServices && isSelectedServices) {
      handleServiceSelectionChange(SERVICE_SELECTION.ALL);
    }
  }, [hasNoServices, isSelectedServices, handleServiceSelectionChange]);

  const handleRetryCategories = useCallback(() => {
    if (salonId) dispatch(getCategories(salonId));
  }, [dispatch, salonId]);

  const handleServiceToggle = useCallback(
    (serviceId) => {
      setValue(RULE_FIELD_KEYS.SERVICE_IDS, toggleServiceId(serviceId, serviceIds));
    },
    [serviceIds, setValue],
  );

  const handleCategoryToggle = useCallback(
    (category) => {
      setValue(RULE_FIELD_KEYS.SERVICE_IDS, toggleCategorySelection(category, serviceIds));
    },
    [serviceIds, setValue],
  );

  const handleFrequencyChange = useCallback(
    (val) => {
      setValue(RULE_FIELD_KEYS.FREQUENCY, val);
      if (val === FORM_FREQUENCY.EVERY_APPOINTMENT) {
        setValue(RULE_FIELD_KEYS.EXPIRATION_PERIOD, EXPIRATION_PERIOD.NEVER);
      }
    },
    [setValue],
  );

  const persist = (rules) => dispatch(feSetConsultationFormRules({ formId, rules }));

  const handleBack = handleSubmit((rules) => {
    if (!hasServicesError) persist(rules);
    history.push(`${builderBasePath}?step=1`);
  });

  const handleSaveAsDraft = handleSubmit((rules) => {
    persist(rules);
    history.push(formsListPath);
  });

  const handleReviewAndActivate = handleSubmit((rules) => {
    persist(rules);
    history.push(`${builderBasePath}?step=3`);
  });

  return (
    <div data-bem="SetRulesPage" data-testid="SetRulesPage" className={cn(CN.component, { [CN.darkMode]: isDarkMode })}>
      <div className={CN.header}>
        <h2 className={CN.title}>{t('consultationForms.rules.pageTitle', { formName: existingForm?.name ?? '' })}</h2>
        <p className={CN.subtitle}>{t('consultationForms.rules.pageSubtitle')}</p>
      </div>

      <div className={CN.section}>
        <span className={CN.sectionLabel}>{t('consultationForms.rules.sendFormLabel')}</span>
        <div className={CN.pillGroup}>
          {[
            { value: FORM_AUTOMATION_TYPES.BEFORE_APPOINTMENT, labelKey: 'consultationForms.rules.triggerBefore', testId: 'SetRulesPage__triggerBefore' },
            { value: FORM_AUTOMATION_TYPES.AFTER_APPOINTMENT, labelKey: 'consultationForms.rules.triggerAfter', testId: 'SetRulesPage__triggerAfter' },
            { value: FORM_AUTOMATION_TYPES.MANUAL, labelKey: 'consultationForms.rules.triggerManual', testId: 'SetRulesPage__triggerManual' },
          ].map((opt) => (
            <button key={opt.value} type="button" data-testid={opt.testId} className={cn(CN.pill, { [CN.pillActive]: triggerType === opt.value })} onClick={() => handleTriggerTypeChange(opt.value)}>
              <span className={CN.pillRadio} aria-hidden="true">
                {triggerType === opt.value ? <CheckCircleIcon className={CN.pillCheckIcon} /> : <div data-testid="SetRulesPage__pillUnselectedCircle" className={CN.pillUnselectedCircle} />}
              </span>
              <span className={CN.pillText}>{t(opt.labelKey)}</span>
            </button>
          ))}
        </div>
      </div>

      {isManual && (
        <div data-testid="SetRulesPage__manualCallout" className={CN.manualCallout}>
          <div className={CN.titleRow}>
            <InformationCircleIcon className={CN.calloutIcon} />
            <span className={CN.calloutTitle}>{t('consultationForms.rules.manualCalloutTitle')}</span>
          </div>
          <span className={CN.calloutText}>{t('consultationForms.rules.manualCalloutBody')}</span>
        </div>
      )}

      {/* Contextual fields — hidden when manual */}
      {!isManual && (
        <>
          <div className={CN.section}>
            <span className={CN.sectionLabel}>{t('consultationForms.rules.timingLabel')}</span>
            <RulesSelect testId="SetRulesPage__triggerTiming" value={triggerTiming} onChange={(val) => setValue(RULE_FIELD_KEYS.TRIGGER_TIMING, val)} options={timingOptions} />
          </div>

          <div className={CN.section}>
            <span className={CN.sectionLabel}>{t('consultationForms.rules.servicesLabel')}</span>
            <div className={CN.pillGroup}>
              <button
                type="button"
                data-testid="SetRulesPage__servicesAll"
                className={cn(CN.pill, { [CN.pillActive]: !isSelectedServices })}
                onClick={() => handleServiceSelectionChange(SERVICE_SELECTION.ALL)}
              >
                <span className={CN.pillRadio} aria-hidden="true">
                  {!isSelectedServices ? <CheckCircleIcon className={CN.pillCheckIcon} /> : <div data-testid="SetRulesPage__pillUnselectedCircle" className={CN.pillUnselectedCircle} />}
                </span>

                <span className={CN.pillText}>{t('consultationForms.rules.servicesAll', { count: totalServicesCount })}</span>
              </button>
              <button
                type="button"
                data-testid="SetRulesPage__servicesSelected"
                className={cn(CN.pill, { [CN.pillActive]: isSelectedServices })}
                onClick={() => handleServiceSelectionChange(SERVICE_SELECTION.SELECTED)}
                disabled={hasNoServices}
              >
                <span className={CN.pillRadio} aria-hidden="true">
                  {isSelectedServices ? <CheckCircleIcon className={CN.pillCheckIcon} /> : <div data-testid="SetRulesPage__pillUnselectedCircle" className={CN.pillUnselectedCircle} />}
                </span>
                <span className={CN.pillText}>
                  {isSelectedServices ? t('consultationForms.rules.servicesSelectedCount', { count: serviceIds.length }) : t('consultationForms.rules.servicesSelected')}
                </span>
              </button>
            </div>

            {isSelectedServices && (
              <div data-testid="SetRulesPage__servicesGrid" className={cn(CN.servicesGrid, CN.servicesGridActive)}>
                {isCategoriesFetching ? (
                  <span data-testid="SetRulesPage__servicesLoading" className={CN.servicesLoading}>
                    {t('consultationForms.rules.servicesLoading')}
                  </span>
                ) : categoriesFetchError ? (
                  <>
                    <span data-testid="SetRulesPage__servicesFetchError" className={CN.servicesFetchError}>
                      {t('consultationForms.rules.servicesFetchError')}
                    </span>
                    <Button
                      variant="outlineForce"
                      onClick={handleRetryCategories}
                      tag="SetRulesPage__servicesRetry"
                      data-testid="SetRulesPage__servicesRetry"
                      className={CN.servicesRetry}
                      noMargin
                      noVerticalMargin
                    >
                      {t('consultationForms.rules.servicesRetry')}
                    </Button>
                  </>
                ) : (
                  <div data-testid="SetRulesPage__servicesColumns" className={CN.servicesColumns}>
                    {serviceCategoryColumns.map((columnCategories, columnIndex) => (
                      <div key={columnIndex} data-testid={`SetRulesPage__servicesColumn-${columnIndex}`} className={CN.servicesColumn}>
                        {columnCategories.map((cat) => {
                          const categoryState = getCategorySelectionState(cat, serviceIds);

                          return (
                            <div key={cat.id} className={CN.categoryGroup}>
                              <div className={CN.categoryRow}>
                                <TypedOption
                                  type="simple-checkbox"
                                  tag={`SetRulesPage__category-${cat.id}`}
                                  checked={categoryState === 'all'}
                                  indeterminate={categoryState === 'partial'}
                                  onCheck={() => handleCategoryToggle(cat)}
                                >
                                  <span className={CN.categoryName}>{cat.name}</span>
                                </TypedOption>
                              </div>
                              {cat.services.map((service) => (
                                <div key={service.id} className={CN.serviceRow}>
                                  <TypedOption
                                    type="simple-checkbox"
                                    tag={`SetRulesPage__service-${service.id}`}
                                    checked={serviceIds.includes(service.id)}
                                    onCheck={() => handleServiceToggle(service.id)}
                                  >
                                    <span className={CN.serviceLabel}>{service.name}</span>
                                  </TypedOption>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {hasServicesError && (
              <span data-testid="SetRulesPage__servicesError" className={CN.servicesError}>
                {t('consultationForms.rules.servicesError')}
              </span>
            )}
          </div>

          <div className={CN.section}>
            <span className={CN.sectionLabel}>{t('consultationForms.rules.frequencyLabel')}</span>
            <RulesSelect testId="SetRulesPage__frequency" value={frequency} onChange={handleFrequencyChange} options={FREQUENCY_OPTIONS} />
            {isEveryAppointment && (
              <div data-testid="SetRulesPage__everyAppointmentCallout" className={CN.expiryCallout}>
                <InformationCircleIcon className={CN.calloutIcon} />
                <span className={CN.calloutText}>{t('consultationForms.rules.everyAppointmentCallout')}</span>
              </div>
            )}
          </div>
        </>
      )}

      {showExpiryField && (
        <div className={CN.section}>
          <span className={CN.sectionLabel}>{t('consultationForms.rules.expiryLabel')}</span>
          <RulesSelect testId="SetRulesPage__expirationPeriod" value={expirationPeriod} onChange={(val) => setValue(RULE_FIELD_KEYS.EXPIRATION_PERIOD, val)} options={EXPIRATION_PERIOD_OPTIONS} />
          {expirationPeriod !== EXPIRATION_PERIOD.NEVER && (
            <div data-testid="SetRulesPage__expiryCallout" className={CN.expiryCallout}>
              <InformationCircleIcon className={CN.calloutIcon} />
              <span className={CN.calloutText}>{t(isManual ? 'consultationForms.rules.manualExpiryCallout' : 'consultationForms.rules.expiryCallout', { months: expiryMonths })}</span>
            </div>
          )}
        </div>
      )}

      <div className={CN.footer}>
        {!isMobile && (
          <Button variant="empty" onClick={handleBack} tag="SetRulesPage__back" data-testid="SetRulesPage__back" className={CN.backButton} noMargin noVerticalMargin>
            <ArrowLongLeftIcon className={CN.backIcon} />
            <span className={CN.backButtonText}>{t('consultationForms.rules.backToBuilder')}</span>
          </Button>
        )}

        <div className={CN.footerRight}>
          <Button
            variant="outlineForce"
            onClick={handleSaveAsDraft}
            tag="SetRulesPage__saveDraft"
            data-testid="SetRulesPage__saveDraft"
            disabled={isFooterBlocked}
            xSpace
            noMargin
            noVerticalMargin
            className={CN.saveDraftButton}
          >
            {t('consultationForms.rules.saveDraft')}
          </Button>
          <Button
            variant="primaryForce"
            onClick={handleReviewAndActivate}
            tag="SetRulesPage__reviewAndActivate"
            data-testid="SetRulesPage__reviewAndActivate"
            disabled={isFooterBlocked}
            xSpace
            noMargin
            noVerticalMargin
            className={CN.reviewButton}
          >
            <span className={CN.reviewButtonContent}>
              <span>{t('consultationForms.rules.reviewAndActivate')}</span>
              <ArrowRightIcon className={CN.arrowRightIcon} />
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
